import { Content, Header, Page } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import { usePortfolioQueries } from './usePortfolioQueries';
import { usePortfolioAccess } from './usePortfolioAccess';
import { usePortfolioRoutes } from './usePortfolioRoutes';
import { usePortfolioLabels } from './usePortfolioLabels';
import {
  usePortfolioFilters,
  usePortfolioFilterReconciliation,
} from './usePortfolioFilters';
import { usePortfolioSelection } from './usePortfolioSelection';
import { usePortfolioDeletion } from './usePortfolioDeletion';
import {
  PortfolioContent,
  PortfolioCopy,
  PortfolioError,
} from './PortfolioContent';

export { resolveCreationEntryState } from './usePortfolioAccess';
export type { CreationEntryState } from './usePortfolioAccess';

export interface ActivityListPageProps extends Partial<PortfolioCopy> {
  onCreate?: () => void;
}

function portfolioCopy({
  portfolioTitle = 'Spassu Code',
  portfolioSubtitle = 'Desenvolvimento assistido por IA',
  supportText = 'Atividades de desenvolvimento assistido por IA conduzidas pelo Control Plane.',
  emptyDescription = 'Crie uma nova atividade para começar a usar o Spassu Code.',
}: ActivityListPageProps): PortfolioCopy {
  return { portfolioTitle, portfolioSubtitle, supportText, emptyDescription };
}

export const ActivityListPage = (props: ActivityListPageProps = {}) => {
  const api = useApi(spassuCodeApiRef);
  const routes = usePortfolioRoutes(props.onCreate);
  const access = usePortfolioAccess(routes.openCreation);
  const filterState = usePortfolioFilters();
  const queries = usePortfolioQueries(api, filterState.filters);
  usePortfolioFilterReconciliation(
    filterState,
    queries.filterOptions,
    queries.filterOptionsReady,
  );
  const selection = usePortfolioSelection(queries.activities);
  const labels = usePortfolioLabels(
    api,
    queries.activities,
    queries.filterOptions.creators,
  );
  const deletion = usePortfolioDeletion(api, selection, queries);
  const copy = portfolioCopy(props);
  const error = deletion.actionError ?? queries.error;
  return (
    <Page themeId="tool">
      <Header title={copy.portfolioTitle} subtitle={copy.portfolioSubtitle} />
      <Content>
        {error ? (
          <PortfolioError error={error} onRetry={deletion.loadActivities} />
        ) : (
          <PortfolioContent
            copy={copy}
            access={access}
            routes={routes}
            labels={labels}
            filterState={filterState}
            queries={queries}
            selection={selection}
            deletion={deletion}
            busy={deletion.busy}
          />
        )}
      </Content>
    </Page>
  );
};
